/**
 * The serializable error shape `createSaga` (`store/utils/saga.ts`) dispatches on
 * its error action. It is a plain object, never an `Error` instance, because the
 * Redux store should only hold serializable values. Exported so both saga trees
 * and slice authors share one source of truth for the payload contract.
 */
export type SagaErrorPayload = {
  message: string;
  stack?: string;
  code?: number;
};

/**
 * Extracts a human-readable message from a saga error payload.
 *
 * `createSaga` (`store/utils/saga.ts`) dispatches its error action with a plain,
 * serializable object `{ message, stack }` — never an `Error` instance — even
 * though the slice reducer types the payload as `Error`. Because of that
 * mismatch, `payload instanceof Error` is always false at runtime and a naive
 * `String(payload)` renders the toast as `"[object Object]"`, swallowing the
 * real message (e.g. "Cannot commit in draft mode"). See APPS-4625.
 *
 * This normalizes the shapes a saga error payload can take — an `Error`, the
 * serialized `{ message }` object, or a string — to the underlying message,
 * falling back to `String()` only when no usable message is present.
 */
export function getSagaErrorMessage(payload: unknown): string {
  if (payload instanceof Error) {
    return payload.message;
  }
  if (
    typeof payload === "object" &&
    payload !== null &&
    "message" in payload &&
    typeof payload.message === "string"
  ) {
    return payload.message;
  }
  return String(payload);
}

/**
 * Extracts a numeric error code from a saga error payload, if present.
 *
 * `createSaga` forwards the originating error's `code` (e.g. an `HttpError`'s
 * HTTP status) alongside `message`/`stack`, so slice reducers can branch on it.
 * This works on both the live error (inside a saga's `catch`) and the
 * serialized payload (inside a reducer), since both expose a numeric `code`.
 * A reducer that instead does `payload instanceof HttpError` never matches,
 * because the dispatched payload is a plain object, not an `HttpError`.
 */
export function getSagaErrorCode(payload: unknown): number | undefined {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "code" in payload &&
    typeof payload.code === "number"
  ) {
    return payload.code;
  }
  return undefined;
}
